// Detalle de una entrega: datos de SAP, artículos, y el flujo del repartidor
// (cargar → en ruta → en sitio → entregada / no entregada) con sello GPS.
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { PageHeader } from '@/components/ui/PageHeader'
import { Button } from '@/components/ui/Button'
import { useAuth } from '@/hooks/useAuth'
import {
  STATUS_CLASS,
  STATUS_LABEL,
  changeDeliveryStatus,
  formatMoney,
  getDelivery,
  rescheduleDelivery,
} from '@/services/deliveries'
import { listEvidence, STAGE_LABEL } from '@/services/evidence'
import { getAcuse } from '@/services/acuse'
import EvidenceCapture from '@/pages/evidence/EvidenceCapture'
import AcuseFlow from '@/pages/acuse/AcuseFlow'
import { AcuseActions } from '@/components/AcuseActions'
import type { Delivery, DeliveryStatus } from '@/types'

/** Enlace de navegación a partir del texto de la dirección.
 *  A propósito NO geocodificamos: eso exigiría una API de pago. Google Maps resuelve
 *  la dirección solo, y en el celular abre la app de mapas (o Waze si es la de por defecto). */
function mapsUrl(address: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
}

/** Posición actual (best-effort: si el usuario no da permiso, se marca sin GPS). */
function currentPosition(): Promise<{ latitude?: number; longitude?: number; accuracy?: number }> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve({})
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({
        latitude: p.coords.latitude,
        longitude: p.coords.longitude,
        accuracy: p.coords.accuracy,
      }),
      () => resolve({}),
      { enableHighAccuracy: true, timeout: 8000 },
    )
  })
}

// Siguiente paso del repartidor según el estado actual.
const NEXT: Partial<Record<DeliveryStatus, { to: DeliveryStatus; label: string }>> = {
  asignada: { to: 'en_ruta', label: '🚚 Cargada · salir a ruta' },
  en_ruta: { to: 'en_sitio', label: '📍 Llegué a la propiedad' },
  en_sitio: { to: 'entregada', label: '✅ Entregada' },
}

export default function DeliveryDetail() {
  const { id = '' } = useParams()
  const { user } = useAuth()
  const qc = useQueryClient()
  const [failing, setFailing] = useState(false)
  const [rescheduling, setRescheduling] = useState(false)

  const { data: d, isLoading } = useQuery({
    queryKey: ['delivery', id],
    queryFn: () => getDelivery(id),
  })

  const { data: evidence } = useQuery({
    queryKey: ['evidence', id],
    queryFn: () => listEvidence(id),
  })
  const { data: acuse } = useQuery({ queryKey: ['acuse', id], queryFn: () => getAcuse(id) })

  const advance = useMutation({
    mutationFn: async (payload: { status: DeliveryStatus; note?: string }) => {
      const pos = await currentPosition()
      return changeDeliveryStatus(id, { ...payload, ...pos })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['delivery', id] })
      qc.invalidateQueries({ queryKey: ['deliveries'] })
      setFailing(false)
    },
  })

  if (isLoading || !d) return <p className="p-8 text-center text-gray-500">Cargando…</p>

  const isMine = d.driver_id === user?.id
  const isManager = user?.role === 'admin' || user?.role === 'almacen'
  const next = NEXT[d.status]

  return (
    <div className="space-y-4">
      <PageHeader
        title={`Entrega #${d.sap_doc_num}`}
        subtitle={d.card_name ?? undefined}
        actions={
          <span className={`rounded-full px-3 py-1 text-sm font-medium ${STATUS_CLASS[d.status]}`}>
            {STATUS_LABEL[d.status]}
          </span>
        }
      />

      {/* Cómo llegar — lo primero que necesita el repartidor */}
      {d.ship_address && (
        <a href={mapsUrl(d.ship_address)} target="_blank" rel="noreferrer" className="block">
          <Button className="w-full py-3 text-base">🧭 Cómo llegar</Button>
        </a>
      )}

      {/* Datos */}
      <div className="grid gap-4 sm:grid-cols-2">
        <Info label="Dirección de entrega" value={d.ship_address} />
        <Info label="Almacén" value={d.warehouse} />
        <Info label="Importe" value={formatMoney(d.total_doc, d.currency)} />
        <Info label="Referencia del cliente" value={d.num_at_card} />
        <Info label="Fecha programada" value={d.scheduled_date} />
        <Info
          label="Comprometida al cliente"
          value={d.sap_ship_date ?? "Sin pedido de origen"}
        />
        <Info label="Repartidor" value={d.driver?.name ?? 'Sin asignar'} />
      </div>

      {d.comments && (
        <div className="rounded-xl bg-amber-50 p-3 text-sm text-amber-900 ring-1 ring-amber-200">
          <b>Comentarios de SAP:</b> {d.comments}
        </div>
      )}

      {d.last_fail_reason && (
        <div className="rounded-xl bg-red-50 p-3 text-sm text-red-900 ring-1 ring-red-200">
          <b>Último intento fallido:</b> {d.last_fail_reason}
        </div>
      )}

      {/* Artículos */}
      <div className="overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-black/5">
        <h2 className="border-b border-gray-100 px-4 py-3 font-semibold text-gray-900">
          Artículos ({d.lines?.length ?? 0})
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-2">Código</th>
                <th className="px-4 py-2">Descripción</th>
                <th className="px-4 py-2 text-right">Cant.</th>
                <th className="px-4 py-2 text-right">Importe</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(d.lines ?? []).map((l, i) => (
                <tr key={i}>
                  <td className="px-4 py-2 font-mono text-xs">{l.item_code}</td>
                  <td className="px-4 py-2">{l.description}</td>
                  <td className="px-4 py-2 text-right">{l.quantity}</td>
                  <td className="px-4 py-2 text-right">{formatMoney(l.line_total_doc, d.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Acciones del repartidor */}
      {isMine && next && (
        <div className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-black/5">
          <h2 className="mb-3 font-semibold text-gray-900">Siguiente paso</h2>
          <div className="flex flex-wrap gap-2">
            <Button
              size="lg"
              onClick={() => advance.mutate({ status: next.to })}
              disabled={advance.isPending}
            >
              {advance.isPending ? 'Registrando…' : next.label}
            </Button>
            {(d.status === 'en_ruta' || d.status === 'en_sitio') && (
              <Button variant="danger" size="lg" onClick={() => setFailing(true)}>
                ⚠️ No se pudo entregar
              </Button>
            )}
          </div>
          <p className="mt-2 text-xs text-gray-500">
            Se registra tu ubicación al marcar (si diste permiso).
          </p>
        </div>
      )}

      {failing && (
        <ReasonBox
          title="¿Por qué no se pudo entregar?"
          confirmLabel="Registrar"
          onCancel={() => setFailing(false)}
          onConfirm={(note) => advance.mutate({ status: 'no_entregada', note })}
          pending={advance.isPending}
        />
      )}

      {/* Evidencia fotográfica */}
      {(isMine || isManager) && (
        <div className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-black/5">
          <h2 className="mb-3 font-semibold text-gray-900">
            Evidencia ({evidence?.items.length ?? 0})
          </h2>
          {!!evidence?.items.length && (
            <div className="mb-4 grid grid-cols-3 gap-2 sm:grid-cols-4">
              {evidence.items.map((e) => (
                <a key={e.id} href={e.file_url} target="_blank" rel="noreferrer" className="block">
                  <img src={e.file_url} alt={e.stage} className="h-24 w-full rounded-lg object-cover" />
                  <p className="mt-1 truncate text-[11px] text-gray-500">{STAGE_LABEL[e.stage]}</p>
                </a>
              ))}
            </div>
          )}
          {isMine && (d.status === 'en_ruta' || d.status === 'en_sitio') && (
            <EvidenceCapture
              deliveryId={id}
              onUploaded={() => qc.invalidateQueries({ queryKey: ['evidence', id] })}
            />
          )}
        </div>
      )}

      {/* Acuse: firma del cliente + PDF */}
      {(isMine || isManager) && (
        <div className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-black/5">
          <h2 className="mb-3 font-semibold text-gray-900">Acuse</h2>
          {acuse ? (
            <div className="rounded-lg bg-green-50 p-3 ring-1 ring-inset ring-green-200">
              <p className="font-medium text-green-800">✓ {acuse.folio}</p>
              <p className="text-xs text-gray-600">
                Recibió: {acuse.client_name} · {new Date(acuse.signed_at).toLocaleString('es-MX')}
              </p>
              {acuse.pdf_url && (
                <div className="mt-2">
                  <AcuseActions url={acuse.pdf_url} folio={acuse.folio} />
                </div>
              )}
            </div>
          ) : isMine && d.status === 'en_sitio' ? (
            <AcuseFlow
              deliveryId={id}
              onDone={() => {
                qc.invalidateQueries({ queryKey: ['acuse', id] })
                qc.invalidateQueries({ queryKey: ['delivery', id] })
              }}
            />
          ) : (
            <p className="text-sm text-gray-500">
              El acuse se firma cuando el repartidor marca “En sitio”.
            </p>
          )}
        </div>
      )}

      {/* Acciones del almacén */}
      {isManager && (
        <div className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-black/5">
          <h2 className="mb-3 font-semibold text-gray-900">Almacén</h2>
          <Button variant="secondary" onClick={() => setRescheduling(true)}>
            🗓️ Reprogramar fecha
          </Button>
          {d.reschedule_count > 0 && (
            <p className="mt-2 text-xs text-gray-500">
              Reprogramada {d.reschedule_count} vez(ces).
            </p>
          )}
        </div>
      )}

      {rescheduling && (
        <RescheduleBox delivery={d} onClose={() => setRescheduling(false)} />
      )}
    </div>
  )
}

function Info({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="rounded-xl bg-white p-3 shadow-sm ring-1 ring-black/5">
      <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
      <p className="mt-0.5 text-sm text-gray-900">{value || '—'}</p>
    </div>
  )
}

function ReasonBox({
  title,
  confirmLabel,
  onCancel,
  onConfirm,
  pending,
}: {
  title: string
  confirmLabel: string
  onCancel: () => void
  onConfirm: (note: string) => void
  pending: boolean
}) {
  const [note, setNote] = useState('')
  return (
    <div className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-red-200">
      <h3 className="font-semibold text-gray-900">{title}</h3>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={3}
        placeholder="Ej. el hotel no recibió, almacén cerrado, dirección incorrecta…"
        className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
      />
      <div className="mt-3 flex justify-end gap-2">
        <Button variant="secondary" onClick={onCancel}>Cancelar</Button>
        <Button variant="danger" onClick={() => onConfirm(note)} disabled={!note.trim() || pending}>
          {confirmLabel}
        </Button>
      </div>
    </div>
  )
}

function RescheduleBox({ delivery, onClose }: { delivery: Delivery; onClose: () => void }) {
  const qc = useQueryClient()
  const [date, setDate] = useState(delivery.scheduled_date ?? '')
  const [reason, setReason] = useState('')

  const save = useMutation({
    mutationFn: () => rescheduleDelivery(delivery.id, { scheduled_date: date, reason: reason || undefined }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['delivery', delivery.id] })
      qc.invalidateQueries({ queryKey: ['deliveries'] })
      onClose()
    },
  })

  return (
    <div className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-black/5">
      <h3 className="font-semibold text-gray-900">Reprogramar entrega</h3>
      <label className="mt-3 block text-sm font-medium text-gray-700">Nueva fecha</label>
      <input
        type="date"
        value={date}
        onChange={(e) => setDate(e.target.value)}
        className="mt-1 w-full max-w-xs rounded-lg border border-gray-300 px-3 py-2 text-sm"
      />
      <label className="mt-3 block text-sm font-medium text-gray-700">Motivo (opcional)</label>
      <input
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
      />
      {save.isError && (
        <p className="mt-2 text-sm text-red-700">
          {(save.error as { message?: string })?.message ?? 'No se pudo reprogramar.'}
        </p>
      )}
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>Cancelar</Button>
        <Button onClick={() => save.mutate()} disabled={!date || save.isPending}>
          {save.isPending ? 'Guardando…' : 'Reprogramar'}
        </Button>
      </div>
    </div>
  )
}
